import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { idSchema, revisionSchema, text } from '../validators'
import { PARTICIPATION_REQUEST_TYPES, PARTICIPATION_STATES } from '../vocab'

/**
 * Participation requests (P-PART-001..004, R-11, PART-*). Nothing is sent
 * externally: an acknowledgment is not a booking and no one is contacted.
 */

export const PARTICIPATION_NOTE_MAX = 2000

export const participationCreateSchema = z.strictObject({
  engagement_id: idSchema,
  request_type: z.enum(PARTICIPATION_REQUEST_TYPES.values),
  note: text(8, PARTICIPATION_NOTE_MAX),
  meeting_id: idSchema.nullable().optional(),
})
export type ParticipationCreate = z.infer<typeof participationCreateSchema>

/**
 * Manager transitions need a response of 8+ characters (checked by the state
 * machine so the error is `reason_required`); a withdrawal note is optional.
 */
export const participationTransitionSchema = z.strictObject({
  revision: revisionSchema,
  note: z.string().max(PARTICIPATION_NOTE_MAX).nullable().optional(),
})
export type ParticipationTransitionInput = z.infer<typeof participationTransitionSchema>

export const participationReassignSchema = z.strictObject({
  revision: revisionSchema,
  manager_id: idSchema,
  note: text(8, PARTICIPATION_NOTE_MAX),
})
export type ParticipationReassignInput = z.infer<typeof participationReassignSchema>

/** URL action → state-machine action. */
export const PARTICIPATION_ROUTE_ACTIONS = {
  acknowledge: 'acknowledge',
  'consent-pending': 'request_company_consent',
  confirm: 'confirm',
  decline: 'decline',
  withdraw: 'withdraw',
} as const
export type ParticipationRouteAction = keyof typeof PARTICIPATION_ROUTE_ACTIONS

export const PARTICIPATION_ACTION_LABELS: Record<ParticipationRouteAction | 'reassign', string> = {
  acknowledge: 'Acknowledge',
  'consent-pending': 'Ask for company consent',
  confirm: 'Confirm',
  decline: 'Decline',
  withdraw: 'Withdraw request',
  reassign: 'Reassign',
}

export const participationListQuerySchema = pageQuerySchema.extend({
  engagement_id: idSchema.optional(),
  status: z.enum(PARTICIPATION_STATES.values).optional(),
  /** `open` = requested, acknowledged or awaiting company consent. */
  open: z.enum(['true', 'false']).optional(),
})
export type ParticipationListQuery = z.infer<typeof participationListQuerySchema>

export const PARTICIPATION_INELIGIBILITY = {
  engagement_closed: 'This case is closed. Ask the manager a question instead.',
  meeting_unavailable: 'This meeting is no longer open for requests.',
  meeting_started: 'This meeting has already started.',
  cutoff_passed: 'The participation deadline for this meeting has passed.',
  duplicate: 'You already have an open request for this engagement.',
} as const
export type ParticipationIneligibility = keyof typeof PARTICIPATION_INELIGIBILITY

/** Server-computed `can_request` (P-PART-002, D-14): the browser never decides. */
export const participationEligibilitySchema = z.object({
  can_request: z.boolean(),
  reason: z
    .enum(Object.keys(PARTICIPATION_INELIGIBILITY) as [ParticipationIneligibility])
    .nullable(),
  message: z.string().nullable(),
  /** Request types the caller has no open or confirmed request for (case-level). */
  available_types: z.array(z.enum(PARTICIPATION_REQUEST_TYPES.values)),
  evaluated_at: z.string(),
})
export type ParticipationEligibility = z.infer<typeof participationEligibilitySchema>

const engagementRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  issuer_name: z.string(),
})

const meetingRefSchema = z.object({
  id: z.string(),
  title: z.string(),
  starts_at: z.string(),
})

export const investorParticipationEventSchema = z.object({
  id: z.string(),
  action: z.string(),
  from_status: z.enum(PARTICIPATION_STATES.values).nullable(),
  to_status: z.enum(PARTICIPATION_STATES.values),
  actor_name: z.string(),
  actor_role: z.enum(['investor', 'manager']),
  note: z.string().nullable(),
  created_at: z.string(),
})
export type InvestorParticipationEvent = z.infer<typeof investorParticipationEventSchema>

/** Investor allowlist: display names only, no user ids (P-ID-006). */
export const investorParticipationSchema = z.object({
  id: z.string(),
  request_type: z.enum(PARTICIPATION_REQUEST_TYPES.values),
  note: z.string(),
  status: z.enum(PARTICIPATION_STATES.values),
  engagement: engagementRefSchema.nullable(),
  meeting: meetingRefSchema.nullable(),
  requested_by_name: z.string(),
  requested_by_me: z.boolean(),
  manager_name: z.string().nullable(),
  response: z.string().nullable(),
  responded_at: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  revision: z.number().int(),
  can_withdraw: z.boolean(),
  events: z.array(investorParticipationEventSchema),
  /** Present on the create response when an open request already existed (PART-011). */
  deduplicated: z.boolean().optional(),
})
export type InvestorParticipation = z.infer<typeof investorParticipationSchema>

const personSchema = z.object({ id: z.string(), name: z.string() })

export const staffParticipationEventSchema = investorParticipationEventSchema
  .omit({ actor_name: true })
  .extend({
    actor: personSchema,
    from_manager: personSchema.nullable(),
    to_manager: personSchema.nullable(),
  })
export type StaffParticipationEvent = z.infer<typeof staffParticipationEventSchema>

export const staffParticipationSchema = z.object({
  id: z.string(),
  request_type: z.enum(PARTICIPATION_REQUEST_TYPES.values),
  note: z.string(),
  status: z.enum(PARTICIPATION_STATES.values),
  org: z.object({ id: z.string(), name: z.string() }),
  engagement: engagementRefSchema.extend({ status: z.string() }),
  meeting: meetingRefSchema.nullable(),
  requested_by: personSchema,
  assigned_manager: personSchema.nullable(),
  response: z.string().nullable(),
  responded_at: z.string().nullable(),
  withdrawn_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  revision: z.number().int(),
  /** Actions the caller may take now (the server re-checks on submit). */
  available_actions: z.array(
    z.enum(['acknowledge', 'consent-pending', 'confirm', 'decline', 'reassign']),
  ),
  events: z.array(staffParticipationEventSchema),
})
export type StaffParticipation = z.infer<typeof staffParticipationSchema>
